import { useEffect, useMemo, useState } from "react";
import { Box, LinearProgress, useMediaQuery, useTheme } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_Row,
  MRT_GlobalFilterTextField as MRTGlobalFilterTextField,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton
} from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ShowWorkplaceButton from "../../components/EquipmentList/show-workplace.component";
import { FmDeviceDialog as BookingPlanDialog } from "../../components/EquipmentList/FilterableEquipmentList/FmDeviceDialog/FmDeviceDialog";
import usePlaceApprovalColumns from "./hooks/usePlaceApprovalColumns";
import { useRemoteInactivateBookings } from "../../hooks/Remote/Booking/useRemoteInactivateBookings";
import { useRemoteFetchUserApprovalPlaceBookings } from "../../hooks/Remote/Approval/useRemoteFetchUserApprovalPlaceBookings";
import { BookingType } from "../Booking-Form/typings/booking-inputs";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";
import { handleMultiDeleteBookings } from "../Schedule/schedule.functions";
import { BookingApproval } from "./typings/booking-approval-type";
import { commonLocalization } from "../../functions/tableLocalization";
import { BookingApprovalTopToolbar as PlaceBookingApprovalTopToolbar } from "./booking-approval-toptoolbar.component";
import TableDeleteConfirmDialog from "../../components/Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { withSeeditMrtInitialState } from "../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../themes/use-seedit-mrt-table-options";

export type PlaceApprovalRow = {
  bookingId: number;
  bookingType: BookingType;
  scheduleId: number;
  inventoryId: number;
  userBookedFor: string;
  start: string;
  end: string;
  timezone: string;
  weekday: string;
  locationName: string;
  floorName: string;
  categoryName: string;
};
export interface PlaceApprovalTableState {
  columns: Array<MRT_ColumnDef<PlaceApprovalRow>>;
}

/**
 * a table to display all desk bookings.
 * A supervisor can reject a booking here.
 * @constructor
 */
function PlaceBookingApproval({
  isSupervisorPerspective
}: Readonly<{ isSupervisorPerspective: boolean }>) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const { dateFormat, timeFormat } = useTimeFormat();
  const localeFullFormat = dateFormat + " " + timeFormat;

  const [open, setOpen] = useState<boolean>(false);
  const [selectedWorkplaceInventoryId, setSelectedWorkplaceInventoryId] = useState<number>();
  const [floorPlan, setFloorPlan] = useState<any>();
  const [columns, setColumns] = useState<PlaceApprovalTableState>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const {
    data: approvalData,
    refetch: refetchApprovalData,
    isLoading,
    isFetching
  } = useRemoteFetchUserApprovalPlaceBookings({ isSupervisorPerspective });
  const { mutate: inactivateBookings, status: inactivateBookingsStatus } =
    useRemoteInactivateBookings(t("Successfully rejected the workplace(s)"));
  const isRejectingBookings = inactivateBookingsStatus === "loading";

  const translatedMobileWorking = t("Mobile Working").toUpperCase();
  const checkMobileWorking = (str: string) =>
    str === "Mobile Working" ? t("Mobile Working") : str;

  // Button to display a workplace in the Facility Manager
  const renderButton = (params: MRT_Row<PlaceApprovalRow>["original"]) => {
    if (
      (params.locationName.toUpperCase() ||
        params.floorName.toUpperCase() ||
        params.categoryName.toUpperCase()) === translatedMobileWorking
    )
      return null;

    return (
      <ShowWorkplaceButton
        setSelectedWorkplaceInventoryId={setSelectedWorkplaceInventoryId}
        inventoryId={params.inventoryId}
        params={{
          id: params.bookingId,
          locationName: params.locationName,
          floorName: params.floorName
        }}
        setFloorPlan={setFloorPlan}
        setOpen={setOpen}
      />
    );
  };

  function handleInactiveBookings(selected: BookingApproval[]) {
    handleMultiDeleteBookings({
      selectedSchedules: selected.map(s => ({
        id: s.bookingId,
        bookingType: s.bookingType
      })),
      inactivateBookings
    });
  }

  const fetchColumns = usePlaceApprovalColumns({ renderButton, dateFormat, localeFullFormat });

  useEffect(() => {
    setColumns(() => fetchColumns());
  }, [i18n.language]);

  useEffect(() => {
    if (inactivateBookingsStatus === "success") {
      setIsDeleteModalOpen(false);
      table.resetRowSelection();
      refetchApprovalData();
    }

    if (inactivateBookingsStatus === "error") {
      enqueueSnackbar(t("There was an error rejecting the workplace(s)"), { variant: "error" });
    }
  }, [inactivateBookingsStatus]);

  const data = useMemo<PlaceApprovalRow[]>(() => {
    if (!approvalData) return [];

    return approvalData.map(d => ({
      ...d,
      floorName: checkMobileWorking(d.floorName),
      workplaceCategory: checkMobileWorking(d.categoryName),
      locationName: checkMobileWorking(d.locationName),
      weekday: t(d.weekday)
    }));
  }, [approvalData, i18n.language, t]);

  const showLoadingOverlay = isLoading || (isFetching && data.length === 0);
  const showTableProgress = isLoading || isFetching || isRejectingBookings;
  const seeditMrtOptions = useSeeditMrtTableOptions<PlaceApprovalRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: columns?.columns ?? [],
    data,
    enableDensityToggle: false,
    autoResetPageIndex: false,
    enableFullScreenToggle: false,
    enableRowSelection: true,
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true
    }),
    muiSearchTextFieldProps: {
      inputProps: {
        style: {
          paddingTop: "12px",
          paddingBottom: "10px"
        }
      }
    },
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no bookings of your employees yet.")
    },
    state: {
      isLoading: showLoadingOverlay,
      showProgressBars: showTableProgress
    },
    positionToolbarAlertBanner: "none",
    renderTopToolbar: ({ table }) => {
      return (
        <Box>
          {showTableProgress && <LinearProgress data-testid="place-approval-table-progress" />}
          <Box
            sx={{
              display: "flex",
              gap: "0.5rem",
              p: "8px",
              justifyContent: "end",
              alignItems: "center"
            }}
          >
            {/* import MRT sub-components */}
            <MRTGlobalFilterTextField table={table} />
            <MRTToggleFiltersButton table={table} />

            {/* inactivate button */}
            <PlaceBookingApprovalTopToolbar
              disabled={table.getSelectedRowModel().flatRows.length <= 0}
              onClick={() => setIsDeleteModalOpen(true)}
            />

            {/* Confirm Deletion Modal */}
            <TableDeleteConfirmDialog
              dialogDesc={t("_tableDeletionConfirmationDesc")}
              isDeleteModalOpen={isDeleteModalOpen}
              setIsDeleteModalOpen={setIsDeleteModalOpen}
              onConfirm={() => {
                const selected = table.getSelectedRowModel().rows.map(row => row.original);
                handleInactiveBookings(selected);
              }}
              deleteStatus={inactivateBookingsStatus}
            />
          </Box>
        </Box>
      );
    }
  });

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MaterialReactTable table={table} />
        </LocalizationProvider>
      </Box>

      <BookingPlanDialog
        open={open}
        handleClose={() => setOpen(false)}
        isMobile={isMobile}
        floorPlan={floorPlan}
        selectedInventoryId={selectedWorkplaceInventoryId}
        dialogTitle="Booking Details"
      />
    </>
  );
}

export default PlaceBookingApproval;
